import { useApiClient } from "@/composables/useApiClient";
import {
  type Item,
  type ItemCreateInput,
  type ItemInput,
  AppEnum,
  AppNamespaceEnum,
  ItemMoveInput,
} from "@/services/fobizzToolsApi";
import { useClassroomScreenStore } from "@/stores/classroomScreen";
import { flash } from "@/utils/flash";
import { type MutationObserverOptions } from "@tanstack/vue-query";

type InvalidateQueryKeys = (AppEnum | string | number)[];

export function useItemQueries() {
  const queryClient = useQueryClient();
  const { discardedItems: discardedItemsService, items: itemsService } =
    useApiClient();
  const { t } = useI18n();

  const useDuplicateItemMutation = ({
    id,
    kind,
    tabId,
  }: {
    id: string;
    kind: Item["kind"];
    tabId: string;
  }) => {
    return useMutation(
      {
        mutationFn: async () => {
          if (!id || !kind) {
            return;
          }
          await itemsService.duplicateItem({
            objId: id,
            objType: kind,
            tabId,
          });
        },
      },
      queryClient
    );
  };

  const useToggleTemplateMutation = ({
    id,
    kind,
    invalidateQueryKeys,
  }: {
    id: string;
    kind: Item["kind"];
    invalidateQueryKeys: InvalidateQueryKeys;
  }) => {
    return useMutation(
      {
        mutationFn: async () => {
          if (!id || !kind) {
            return;
          }
          await itemsService.toggleTemplateItem({
            objId: id,
            objType: kind,
          });
        },
        onSuccess: () => {
          return queryClient.invalidateQueries({
            queryKey: invalidateQueryKeys,
          });
        },
      },
      queryClient
    );
  };

  const useToggleDiscardMutation = ({
    id,
    kind,
    invalidateQueryKeys,
  }: {
    id: string;
    kind: Item["kind"];
    invalidateQueryKeys?: InvalidateQueryKeys;
  }) => {
    return useMutation(
      {
        mutationFn: async () => {
          if (!id || !kind) {
            return;
          }
          await itemsService.toggleDiscardedItem({
            objId: id,
            objType: kind,
          });
        },
        onSuccess: () => {
          if (invalidateQueryKeys) {
            // wait until elastic search index is updated
            setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: invalidateQueryKeys,
              });
            }, 500);
          }
        },
      },
      queryClient
    );
  };

  const useCreateMutation = (
    opts?: MutationObserverOptions<Item, Error, ItemCreateInput>
  ) => {
    return useMutation(
      {
        mutationFn: (requestBody) =>
          itemsService.createItem({
            requestBody,
          }),
        ...opts,
      },
      queryClient
    );
  };

  const useUpdateMutation = (
    {
      id,
      kind,
    }: {
      id: string;
      kind: Item["kind"];
    },
    opts?: MutationObserverOptions<Item, Error, Partial<ItemInput>>
  ) => {
    return useMutation(
      {
        mutationFn: (attrs) =>
          itemsService.updateItem({
            objId: id,
            objType: kind,
            requestBody: attrs,
          }),
        onSuccess: (data) => {
          const appsStore = useAppsStore();
          const app = appsStore.findAppById(kind);
          queryClient.invalidateQueries({
            queryKey: [app?.namespace, "items"],
          });
          queryClient.invalidateQueries({
            queryKey: [app?.namespace, id],
          });
          if (app?.namespace === AppNamespaceEnum.CLASSROOM_SCREEN) {
            const screenStore = useClassroomScreenStore();
            screenStore.$patch({ screen: data });
          }
        },
        ...opts,
      },
      queryClient
    );
  };

  const actionDuplicate = ({ item, tabId }: { item: Item; tabId: string }) => {
    if (!item.kind) return;
    useDuplicateItemMutation({
      id: item.id,
      kind: item.kind,
      tabId: tabId,
    }).mutate();
  };

  const actionToggleTemplate = async ({
    item,
    invalidateQueryKeys,
  }: {
    item: Item;
    invalidateQueryKeys: (AppEnum | string | number)[];
  }) => {
    if (!item.kind) {
      return;
    }

    await useToggleTemplateMutation({
      id: item.id,
      kind: item.kind,
      invalidateQueryKeys,
    }).mutate();

    if (item.template) {
      flash(
        FlashKindEnum.INFO,
        t("flash.templateUnmarkSuccess", { name: item.modelName })
      );
    } else {
      flash(
        FlashKindEnum.INFO,
        t("flash.templateMarkSuccess", { name: item.modelName })
      );
    }
  };

  const actionToggleDiscard = ({
    item,
    invalidateQueryKeys,
  }: {
    item: Item;
    invalidateQueryKeys?: (AppEnum | string | number)[];
  }) => {
    if (!item.kind) {
      return;
    }
    return useToggleDiscardMutation({
      id: item.id,
      kind: item.kind,
      invalidateQueryKeys,
    }).mutate();
  };

  const actionClearDiscarded = ({
    tabId,
    invalidateQueryKeys,
  }: {
    tabId: string;
    invalidateQueryKeys?: (AppEnum | string | number)[];
  }) => {
    return useMutation(
      {
        mutationFn: () => discardedItemsService.deleteDiscardedItems({ tabId }),
        onSuccess: () => {
          if (invalidateQueryKeys) {
            return queryClient.invalidateQueries({
              queryKey: invalidateQueryKeys,
            });
          }
        },
      },
      queryClient
    ).mutate();
  };

  const useMoveToFolderMutation = (
    opts?: MutationObserverOptions<void, Error, ItemMoveInput>
  ) => {
    return useMutation(
      {
        mutationFn: (requestBody) =>
          itemsService.moveItem({
            requestBody,
          }),
        onSuccess: () => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: ["folders"],
            });
          }, 500);
        },
        ...opts,
      },
      queryClient
    );
  };

  const useGetItem = ({ objType, id }: { objType: AppEnum; id: string }) => {
    return useQuery({
      queryKey: [objType, id],
      queryFn: () => itemsService.getItem({ objType, id }),
    });
  };

  return {
    actionDuplicate,
    actionToggleTemplate,
    actionToggleDiscard,
    actionClearDiscarded,
    useUpdateMutation,
    useCreateMutation,
    useMoveToFolderMutation,
    useGetItem,
  };
}
